import React from 'react';

import { useIntl, FormattedMessage } from 'react-intl';
import cx from 'classnames';

import { BodyCopy } from '@xingternal/typography';
import { Button } from '@xingternal/button';
import {
  getTrackingUrl,
  trackClickKununuProfile,
  trackClickKununuCulture,
} from '@xingternal/pages-tracking';

import styles from './Rating.css';

type BoxProps = {
  value: Object | string;
  label?: string;
  valueDetail?: string;
  className?: string;
};

type RatingProps = {
  profileUrl?: string;
  score?: number;
  recommendation?: number;
  isEditor?: boolean;
};

const Box = ({ value, valueDetail, label, className }: BoxProps) => (
  <div className={cx(className, styles.box)}>
    <div className={styles.innerBox}>
      <div className={styles.boxBackground} />
      <BodyCopy className={styles.boxValue}>
        {value}
        {valueDetail && <span>{valueDetail}</span>}
      </BodyCopy>
      <BodyCopy size={'small'} fontWeight="bold" lineHeight='dense'>
        <FormattedMessage id={label} defaultMessage={label} />
      </BodyCopy>
    </div>
  </div>
);

const Rating = ({
  score,
  recommendation,
  profileUrl,
  isEditor,
}: RatingProps) => {
  const intl = useIntl();
  const roundedScore = score ? (Math.round(score * 10) / 10).toFixed(1) : '-';

  const roundedScoreLocale =
    intl.locale === 'de' ? roundedScore.replace('.', ',') : roundedScore;

  const recommendationRate = recommendation
    ? `${Math.round(recommendation * 100)}%`
    : '-';

  return (
    <div className={styles.ratingWrapper}>
      <Box
        className={styles.score}
        value={roundedScoreLocale}
        valueDetail={'/5'}
        label="EP_KUNUNU_RATING"
      />
      <Box
        className={styles.recommendation}
        value={recommendationRate}
        label="EP_KUNUNU_RECOMMENDATION"
      />
      <div className={styles.whatSayContainer}>
        <div className={styles.whatSayLabelContainer}>
          <BodyCopy fontWeight='bold' lineHeight='dense' size={'medium'}>
            <FormattedMessage
              id="EP_KUNUNU_DISCOVER_REVIEWS_CULTURE"
              defaultMessage="EP_KUNUNU_DISCOVER_REVIEWS_CULTURE"
            />
          </BodyCopy>
        </div>
        <div data-cy="read_reviewsBtn" className={styles.whatSayLink}>
          <Button
            onClick={() => trackClickKununuProfile({ isEditor })}
            variant={'secondary'}
            size={'medium'}
            className={styles.secondaryCta}
            href={getTrackingUrl({
              to: profileUrl,
              trackingKey: 'viewKununuProfile',
            })}
            target="_blank"
          >
            <FormattedMessage
              id="EP_KUNUNU_WHAT_SAY_BUTTON"
              defaultMessage="EP_KUNUNU_WHAT_SAY_BUTTON"
            />
          </Button>
          <Button
            onClick={() => trackClickKununuCulture({ isEditor })}
            variant={'tertiary'}
            size={'medium'}
            className={styles.secondaryCta}
            href={getTrackingUrl({
              to: `${profileUrl}/kultur`,
              trackingKey: 'viewKununuCulture',
            })}
            target="_blank"
          >
            <FormattedMessage
              id="EP_KUNUNU_CULTURE_BUTTON"
              defaultMessage="EP_KUNUNU_CULTURE_BUTTON"
            />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Rating;
